import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, ChevronLeft, UserPlus, Users, Trash2 } from 'lucide-react'
import { AddPeopleDialog } from './AddPeopleDialog'
import { MessageBubble } from './MessageBubble'
import { MobileStatus } from './MobileStatus'
import { ReplyBar } from './ReplyBar'
import {
  groupChatQueryOptions,
  chatMessagesQueryOptions,
  sendGroupMessage,
  archiveGroupChat,
} from '@/utils/groupChatQueryOptions'
import type { GroupChatMessage } from '@/types/groupChatTypes'
import { useShellStore } from '@/stores/shellStore'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { useUserStore } from '@/stores/userStore'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

// Strip the leading sender-name prefix the backend adds to relayed SMS
// ("First Last - message", or the legacy "[Name]: message") so the app shows a
// clean bubble with the name as a separate author label.
function stripSenderPrefix(body: string, name?: string | null): string {
  if (name) {
    const dash = `${name} - `
    if (body.startsWith(dash)) return body.slice(dash.length)
  }
  return body.replace(/^\[[^\]]+\]:\s/, '')
}

export function ChatThread({ chatId }: { chatId: string }) {
  const isMobile = useIsMobile()
  const backToList = useShellStore((s) => s.backToList)
  const openComposer = useShellStore((s) => s.openComposer)
  const queryClient = useQueryClient()
  const user = useUserStore((s) => s.User)

  const [message, setMessage] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)

  const { data: chat, status: chatStatus, error } = useQuery(groupChatQueryOptions(chatId))
  const { data: messagesData, status: messagesStatus } = useQuery(chatMessagesQueryOptions(chatId))

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messagesData?.messages])

  const sendMutation = useMutation({
    mutationFn: () => sendGroupMessage(chatId, { message: message.trim() }),
    onSuccess: () => {
      setMessage('')
      queryClient.invalidateQueries({ queryKey: ['chatMessages', chatId] })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: () => archiveGroupChat(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupChats'] })
      openComposer()
    },
  })

  const submit = () => {
    if (message.trim() && !sendMutation.isPending) sendMutation.mutate()
  }

  if (chatStatus === 'pending') {
    return (
      <div className="flex h-full flex-col p-6">
        <Skeleton className="mb-4 h-8 w-48" />
        <Skeleton className="h-full w-full" />
      </div>
    )
  }

  if (chatStatus === 'error' || !chat) {
    return <div className="p-6 text-[13px] text-destructive">{error?.message || 'Chat not found'}</div>
  }

  const messages = messagesData?.messages ?? []
  const isMe = (m: GroupChatMessage) =>
    !!user && (m.author === user.displayName || m.senderName === user.displayName)

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex min-h-[56px] flex-none items-center justify-between gap-2.5 border-b border-border px-[18px] py-[9px]">
        <div className="flex min-w-0 items-center gap-2.5">
          {isMobile && (
            <button type="button" onClick={backToList} className="px-0.5 text-muted-foreground" aria-label="Back">
              <ChevronLeft className="h-[22px] w-[22px]" />
            </button>
          )}
          <div className="min-w-0">
            <div className="truncate text-base font-extrabold">{chat.name}</div>
            <div className="mt-px text-[12px] text-muted-foreground">
              {chat.participantCount} {chat.participantCount === 1 ? 'person' : 'people'} ·{' '}
              {chat.isActive ? 'live chat' : 'archived'}
            </div>
          </div>
        </div>
        <div className="flex flex-none items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Participants"
                className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-border bg-surface-2 text-foreground"
              >
                <Users className="h-4 w-4" />
              </button>
            </SheetTrigger>
            <SheetContent className="bg-surface">
              <SheetHeader>
                <SheetTitle>Participants ({chat.participantCount})</SheetTitle>
              </SheetHeader>
              {/* Leaves room under the list for whichever roster actions this
                  viewer gets: archive alone, or archive plus "Add people". */}
              <ScrollArea
                className={cn(
                  'mt-4',
                  chat.isActive && chat.canManageParticipants
                    ? 'h-[calc(100vh-240px)]'
                    : 'h-[calc(100vh-180px)]',
                )}
              >
                <div className="space-y-2">
                  {chat.participants.map((p) => (
                    <div key={p.id} className="rounded-lg border border-border p-3">
                      <p className="text-sm font-medium">{p.displayName || 'Unknown'}</p>
                      <MobileStatus hasMobile={!!p.phoneNumber} className="mt-0.5 text-xs" />
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {/* Adding someone to a live chat hands them the whole history,
                  so the server only lets the chat's owner do it — and the
                  button is hidden for everyone else rather than failing on
                  click. */}
              {chat.isActive && chat.canManageParticipants && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setAddOpen(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold"
                  >
                    <UserPlus className="h-4 w-4" />
                    Add people
                  </button>
                </div>
              )}
              <div className={cn('mt-4', !chat.isActive && 'hidden')}>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                      Archive chat
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Archive this chat?</AlertDialogTitle>
                      <AlertDialogDescription>
                        The group chat will be removed from your list and the Twilio conversation
                        deleted.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => archiveMutation.mutate()}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Archive
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Messages */}
      <div className="flex flex-1 flex-col gap-[11px] overflow-y-auto bg-chat-bg p-[18px]">
        {messagesStatus === 'pending' ? (
          <div className="space-y-3">
            <Skeleton className="h-14 w-3/5" />
            <Skeleton className="ml-auto h-14 w-2/5" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
            <p>No messages yet…</p>
            <p className="text-sm text-faint">Send a message to start the conversation.</p>
          </div>
        ) : (
          messages.map((m) => {
            const me = isMe(m)
            const senderName = me ? user?.displayName : m.senderName || m.author
            return (
              <MessageBubble
                key={m.sid}
                me={me}
                author={!me ? m.senderName || m.author : undefined}
                body={stripSenderPrefix(m.body, senderName)}
              />
            )
          })
        )}
        <div ref={endRef} />
      </div>

      {chat.isActive ? (
        <ReplyBar
          value={message}
          onChange={setMessage}
          onSubmit={submit}
          disabled={sendMutation.isPending}
          placeholder={`Message ${chat.name}…`}
        />
      ) : (
        // The Twilio conversation behind an archived chat is gone — this is the
        // saved record, kept permanently and readable but not writable.
        <div className="flex flex-none items-center gap-2 border-t border-border bg-surface-2 px-[18px] py-3 text-[12.5px] text-muted-foreground">
          <Archive className="h-4 w-4 flex-none" />
          This chat is archived. It's kept as a record and can't be replied to.
        </div>
      )}

      {/* Rendered outside the participants sheet: a dialog nested inside it
          would be torn down with the sheet the moment the sheet lost focus. */}
      <AddPeopleDialog chat={chat} open={addOpen} onOpenChange={setAddOpen} />
    </div>
  )
}
